import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { NavBar } from '@/components/navbar';
import { Footer } from '@/components/SiteFooter';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { motion } from 'framer-motion';
import { useToast } from '@/hooks/use-toast';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { API_CONFIG } from '@/constants/env';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { ClipboardList, Mail, User } from 'lucide-react';
import SEOHelmet from '@/components/SEOHelmet';
import { Badge } from '@/components/ui/badge';
import PageViewTracker from '@/components/analytics/PageViewTracker';
import { trackEvent } from '@/utils/analytics';
import {GlowBackground} from "@/components/GlowBackground.tsx";
import { SuccessModal } from "@/components/SuccessModal";
import { collectBrowserInfo, type BrowserInfo } from "@/utils/browserInfo";

const formSchema = (t: (key: string) => string) =>
  z.object({
    first_name: z.string().min(1, t('validation.firstNameRequired') || 'First name is required'),
    last_name: z.string().min(1, t('validation.lastNameRequired') || 'Last name is required'),
    email: z.string().email(t('validation.invalidEmail') || 'Invalid email address'),
    company: z.string().min(1, t('validation.companyRequired') || 'Company name is required'),
    subject: z.string().min(1, t('validation.subjectRequired') || 'Subject is required'),
    message: z.string().min(1, t('validation.messageRequired') || 'Message is required'),
    compliance_process: z.string().optional(),
    request_demo: z.enum(['yes', 'no', 'maybe'], {
      message: t('validation.demoSelection') || 'Please select if you would like a demo',
    }),
  });

// Use a type cast to make TypeScript happy with our parameterized schema
type FormData = z.infer<ReturnType<typeof formSchema>>;

const ContactPage = () => {
  const { t, i18n } = useTranslation('common');
  const currentLang = i18n.language;
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLocalhost, setIsLocalhost] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  
  // Function to track field interactions
  const trackFieldInteraction = useCallback((fieldName: string, action: string) => {
    trackEvent('Contact', action, fieldName);
  }, []);

  // Create the validation schema with translations
  const validationSchema = formSchema(t);

  useEffect(() => {
    // Check if running on localhost
    const hostname = window.location.hostname;
    setIsLocalhost(hostname === 'localhost' || hostname === '127.0.0.1');
  }, []);

  const form = useForm<FormData>({
    resolver: zodResolver(validationSchema),
    defaultValues: {
      first_name: '',
      last_name: '',
      email: '',
      company: '',
      subject: '',
      message: '',
      compliance_process: '',
      request_demo: 'no',
    },
  });

  // Set fake data for development environment
  useEffect(() => {
    if (isLocalhost) {
      form.reset({
        first_name: 'John',
        last_name: 'Doe',
        email: 'john.doe@example.com',
        company: 'Acme Corporation',
        subject: 'Question about compliance solutions',
        message:
          'I have some questions about your compliance solutions and how they can help our organization.',
        compliance_process:
          'We currently manage compliance manually with spreadsheets and are looking to automate the process.',
        request_demo: 'maybe',
      });
    }
  }, [isLocalhost, form]);

  const onSubmit = async (data: FormData) => {
    setIsSubmitting(true);
    
    // Collect browser and device information
    const browserInfo = collectBrowserInfo();
    
    // Prepare submission data with browser info
    const submissionData = {
      ...data,
      browser_info: browserInfo
    };
    
    // Track the form submission attempt
    trackEvent('Contact', 'FormSubmit', 'ContactForm', {
      requestDemo: data.request_demo
    });
    
    // Timing start
    const startTime = performance.now();
    
    try {
      const response = await fetch(`${API_CONFIG.API_BASE_URL}/contact/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(submissionData),
      });

      if (!response.ok) {
        // Handle non-200 responses without trying to parse them as JSON
        throw new Error(`Error: ${response.status} ${response.statusText}`);
      }

      // Check if the response has content before trying to parse it
      const contentType = response.headers.get('content-type');
      let responseData;

      if (contentType && contentType.includes('application/json') && response.bodyUsed === false) {
        // Only try to parse JSON if the content type is application/json and body hasn't been read
        const text = await response.text();
        responseData = text ? JSON.parse(text) : {};
      }

      // Track successful form submission
      trackEvent('Contact', 'FormSubmitSuccess', 'ContactForm');

      setIsSubmitting(false);
      setShowSuccessModal(true);
    } catch (error) {
      console.error('Error submitting form:', error);
      let errorMessage = t('contact.notifications.error.description');

      if (error instanceof Error) {
        errorMessage = error.message;
      }

      toast({
        title: t('contact.notifications.error.title'),
        description: errorMessage,
        variant: 'destructive',
      });
      
      // Track form submission error
      trackEvent('Contact', 'FormSubmitError', errorMessage);
      
      setIsSubmitting(false);
    } finally {
      // Track timing for form submission
      const endTime = performance.now();
      const timeTaken = Math.round(endTime - startTime);
      trackTiming('Contact', 'FormSubmission', timeTaken, 'ContactForm');
    }
  };

  return (
    <div className="min-h-screen">
      <SEOHelmet path="/contact" />
      <PageViewTracker path="/contact" title="Contact Verdea - Get in Touch With Our Team" />

      <NavBar />
      <GlowBackground colorScheme={'green'} />

      <main className="relative pt-24 pb-16 min-h-screen overflow-hidden">
        {/* Background Elements - Apple style with green theme */}
        <div className="fixed inset-0 z-0 overflow-hidden pointer-events-none">
          {/* Primary gradient blur - Green theme */}
          <div
            className="absolute top-0 right-0 w-[50vw] h-[50vh] blur-3xl opacity-30"
            style={{
              background:
                'radial-gradient(circle at center, rgba(24, 76, 6, 0.3) 0%, rgba(24, 76, 6, 0) 70%)', // Dark green #184c06
              transform: 'translate(20%, -20%)',
            }}
          />

          {/* Secondary gradient blur */}
          <div
            className="absolute bottom-0 left-0 w-[40vw] h-[40vh] blur-3xl opacity-20"
            style={{
              background:
                'radial-gradient(circle at center, rgba(34, 147, 2, 0.3) 0%, rgba(34, 147, 2, 0) 70%)', // Light green #229302
              transform: 'translate(-20%, 20%)',
            }}
          />

          {/* Subtle dot grid pattern */}
          <div
            className="absolute inset-0 opacity-[0.015]"
            style={{
              backgroundImage: 'radial-gradient(rgba(34, 147, 2, 0.2) 1px, transparent 1px)', // Light green #229302
              backgroundSize: '40px 40px',
            }}
          />
        </div>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="max-w-4xl mx-auto"
        >
          <div className="mb-12 text-center">
            <Badge
              variant="outline"
              className="px-4 py-1.5 mb-6 bg-green-50 border-green-100 text-green-800 text-sm font-medium dark:bg-green-900/20 dark:border-green-800/30 dark:text-green-400"
            >
              {t('contact.badge')}
            </Badge>
            <h1 className="text-4xl md:text-6xl font-medium tracking-tight text-gray-900 dark:text-gray-100 mb-6 leading-tight">
              {t('contact.heading.part1')}
              <br />
              <span className="bg-gradient-to-r from-[#184c06] via-[#1b6504] to-[#229302] bg-clip-text text-transparent font-semibold">
                {t('contact.heading.highlight')}
              </span>
            </h1>
            <p className="text-lg md:text-xl text-gray-500 dark:text-gray-400 mb-8 max-w-3xl mx-auto leading-relaxed">
              {t('contact.subheading')}
            </p>
          </div>

          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className={`space-y-8 ${showSuccessModal ? 'pointer-events-none opacity-75' : ''}`}>
              <div className="bg-background p-6 rounded-lg border border-gray-100 dark:border-gray-800 shadow-sm space-y-6">
                <h2 className="text-xl font-semibold flex items-center gap-2 text-green-600">
                  <User className="h-5 w-5 text-green-600" />
                  {t('contact.personalInfo.title')}
                </h2>
                <div className="grid md:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="first_name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('contact.personalInfo.firstName')}</FormLabel>
                        <FormControl>
                          <Input
                            placeholder={t('contact.personalInfo.firstNamePlaceholder')}
                            {...field}
                            onFocus={() => trackFieldInteraction('first_name', 'FieldFocus')}
                            onBlur={() => trackFieldInteraction('first_name', 'FieldBlur')}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="last_name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('contact.personalInfo.lastName')}</FormLabel>
                        <FormControl>
                          <Input
                            placeholder={t('contact.personalInfo.lastNamePlaceholder')}
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('contact.personalInfo.email')}</FormLabel>
                      <FormControl>
                        <Input
                          type="email"
                          placeholder={t('contact.personalInfo.emailPlaceholder')}
                          {...field}
                          onFocus={() => trackFieldInteraction('email', 'FieldFocus')}
                          onBlur={() => trackFieldInteraction('email', 'FieldBlur')}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="company"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('contact.personalInfo.company')}</FormLabel>
                      <FormControl>
                        <Input
                          placeholder={t('contact.personalInfo.companyPlaceholder')}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="bg-background p-6 rounded-lg border border-gray-100 dark:border-gray-800 shadow-sm space-y-6">
                <h2 className="text-xl font-semibold flex items-center gap-2 text-green-600">
                  <Mail className="h-5 w-5 text-green-600" />
                  {t('contact.messageDetails.title')}
                </h2>
                <FormField
                  control={form.control}
                  name="subject"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('contact.messageDetails.subject')}</FormLabel>
                      <FormControl>
                        <Input
                          placeholder={t('contact.messageDetails.subjectPlaceholder')}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="message"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('contact.messageDetails.message')}</FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder={t('contact.messageDetails.messagePlaceholder')}
                          className="min-h-[120px]"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="bg-background p-6 rounded-lg border border-gray-100 dark:border-gray-800 shadow-sm space-y-6">
                <h2 className="text-xl font-semibold flex items-center gap-2 text-green-600">
                  <ClipboardList className="h-5 w-5 text-green-600" />
                  {t('contact.complianceInfo.title')}
                </h2>
                <FormField
                  control={form.control}
                  name="compliance_process"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('contact.complianceInfo.complianceProcess')}</FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder={t('contact.complianceInfo.complianceProcessPlaceholder')}
                          className="min-h-[120px]"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="request_demo"
                  render={({ field }) => (
                    <FormItem className="space-y-3">
                      <FormLabel>{t('contact.complianceInfo.requestDemo')}</FormLabel>
                      <FormControl>
                        <RadioGroup
                          onValueChange={(value) => {
                            field.onChange(value);
                            // Track demo request selection
                            trackEvent('Contact', 'DemoRequest', value);
                          }}
                          defaultValue={field.value}
                          className="flex flex-col space-y-1"
                        >
                          <FormItem className="flex items-center space-x-3 space-y-0">
                            <FormControl>
                              <RadioGroupItem value="yes" id="demo-yes" />
                            </FormControl>
                            <FormLabel htmlFor="demo-yes">
                              {t('contact.complianceInfo.demoYes')}
                            </FormLabel>
                          </FormItem>
                          <FormItem className="flex items-center space-x-3 space-y-0">
                            <FormControl>
                              <RadioGroupItem value="no" id="demo-no" />
                            </FormControl>
                            <FormLabel htmlFor="demo-no">
                              {t('contact.complianceInfo.demoNo')}
                            </FormLabel>
                          </FormItem>
                          <FormItem className="flex items-center space-x-3 space-y-0">
                            <FormControl>
                              <RadioGroupItem value="maybe" id="demo-maybe" />
                            </FormControl>
                            <FormLabel htmlFor="demo-maybe">
                              {t('contact.complianceInfo.demoMaybe')}
                            </FormLabel>
                          </FormItem>
                        </RadioGroup>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="flex flex-col items-center space-y-4">
                <Button
                  type="submit"
                  className="w-full md:w-auto bg-green-600 hover:bg-green-700 text-white px-8"
                  size="lg"
                  disabled={isSubmitting || showSuccessModal}
                  onClick={() => trackEvent('Contact', 'ButtonClick', 'SubmitButton')}
                >
                  {isSubmitting ? t('contact.button.submitting') : t('contact.button.submit')}
                </Button>
                <p className="text-sm text-slate-500 max-w-lg mt-16 text-center">
                  {t('contact.termsNotice')}{' '}
                  <a 
                    href="/terms" 
                    className="text-green-600 hover:underline"
                    onClick={() => trackEvent('Contact', 'LinkClick', 'Terms of Service')}
                  >
                    {t('contact.termsLink')}
                  </a>{' '}
                  {t('contact.andText')}{' '}
                  <a 
                    href="/privacy" 
                    className="text-green-600 hover:underline"
                    onClick={() => trackEvent('Contact', 'LinkClick', 'Privacy Policy')}
                  >
                    {t('contact.privacyLink')}
                  </a>
                  .
                </p>
              </div>
            </form>
          </Form>
          
          {/* Success Modal */}
          <SuccessModal
            isOpen={showSuccessModal}
            onClose={() => {
              setShowSuccessModal(false);
              form.reset();
            }}
            title={t('contact.notifications.success.title')}
            description={t('contact.notifications.success.description')}
          />
        </motion.div>
      </main>
      <Footer />
    </div>
  );
};

export default ContactPage;
